import {
  AppPageHeader,
  EditIcon,
  InfiniteTable,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  InstallmentsTransaction,
  InstallmentType,
  TransactionData,
  TransactionType,
} from '@/core/domain/financial/BankConciliation/BankConciliation.types';
import { useDebounce } from '@/hooks';
import { useBankConciliationContext } from '@/pages/FinancialPages/BankConciliationPage';
import formatterPrice from '@/utils/number/formatterPrice';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH, tableSorterOrder } from '@/utils/table';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import moment from 'moment';
import { Key, useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  AccountPayableColumnConfig,
  AccountPayableFieldOption,
  AccountPayableOrderColumn,
  AccountPayableSituationColor,
  InstallmentList,
  InstallmentSituation,
  InstallmentSituationType,
  ListInstallmentsQuery,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { useInstallmentsList } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import { Button, Col, Row, Select } from 'antd';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import BillToPayEditModal from '@/components/_common/BillToPayEditModal';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

export default function BankConciliationAccountPayableList() {
  const intl = useIntl();

  const {
    bankAccountId,
    transactionType,
    conciliatesData,
    setConciliatesData,
    transactionSelected,
    installmentsIdsSelected,
  } = useBankConciliationContext();

  const [accountPayableId, setAccountPayableId] = useState<number>();
  const [selectedKeys, setSelectedKeys] = useState<Key[]>(
    transactionSelected?.installments.map(
      (installment) => installment.installmentId,
    ) ?? [],
  );

  const allLoadedItems = useRef(new Map<number, InstallmentList>());

  const [queryParams, setQueryParams] = useState<ListInstallmentsQuery>({
    order: 'DESC',
    bankAccountId,
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const enabled =
    !!transactionSelected && transactionType === TransactionType.debit;

  const { data, isFetching, fetchNextPage, hasNextPage } = useInstallmentsList(
    queryDebounce,
    enabled,
  );

  const items = flatInfiniteData(data);

  useEffect(() => {
    items.forEach((item) => allLoadedItems.current.set(item.id, item));
  }, [items]);

  useEffect(() => {
    if (transactionSelected) {
      setSelectedKeys(
        transactionSelected.installments.map(
          (installment) => installment.installmentId,
        ),
      );

      const hasInstallments = transactionSelected.installments.length > 0;

      setQueryParams((prev) => ({
        ...prev,
        dueDate: hasInstallments ? undefined : transactionSelected?.payDate,
        installmentValue: hasInstallments
          ? undefined
          : transactionSelected?.value,
      }));
    }
  }, [transactionSelected]);

  const toNumber = (id: Key) => {
    const num = Number(id);
    return Number.isNaN(num) ? null : num;
  };

  const isLinkedToAnotherTransaction = (id: number) =>
    installmentsIdsSelected?.includes(id) && !selectedKeys.includes(id);

  const getLinkedTransactionInfo = (id: number) => {
    const transaction = conciliatesData?.find((transaction) =>
      transaction.installments.some(
        (installment) => installment.installmentId === id,
      ),
    );

    return transaction
      ? `${transaction.name} (${moment(transaction.payDate).format(
          'DD/MM/YYYY',
        )})`
      : null;
  };

  const getSelectedKeysVisibleInTable = () => {
    const visibleIds = new Set(items.map((item) => item.id));

    return selectedKeys.filter((id) => {
      const num = toNumber(id);
      return num !== null && visibleIds.has(num);
    });
  };

  function handleSelect(keys: Key[]) {
    const visibleIds = new Set(items.map((item) => item.id));

    const previouslySelected = selectedKeys.filter((id) => {
      const numberKey = toNumber(id);
      return numberKey !== null && !visibleIds.has(numberKey);
    });

    const merged = [...previouslySelected, ...keys];

    setSelectedKeys(merged);
    updateConciliation(merged);
  }

  function updateConciliation(keys: Key[]) {
    if (!transactionSelected) return;

    const selectedInstallments: InstallmentsTransaction[] = keys.flatMap(
      (key) => {
        const id = toNumber(key);
        const item = id ? allLoadedItems.current.get(id) : undefined;

        if (!item) return [];

        return [
          {
            installmentId: item.id,
            accountId: item.accountPayableId,
            codeIdentifier: item.code,
            installmentValue:
              item.installmentValue +
              (item.discountIncrease ?? 0) -
              (item.credit ?? 0),
            dueDate: item.dueDate,
            type: InstallmentType.accountPayable,
            favored: item.creditor,
            isChecked: true,
          },
        ];
      },
    );

    const updatedTransaction: TransactionData = {
      ...transactionSelected,
      installments: selectedInstallments,
    };

    setConciliatesData((prev) => {
      if (!prev) return [updatedTransaction];

      const index = prev.findIndex(
        (transaction) =>
          transaction.transactionId === updatedTransaction.transactionId,
      );

      if (index === -1) return [...prev, updatedTransaction];

      const copy = [...prev];
      copy[index] = updatedTransaction;
      return copy;
    });
  }

  const columns: ColumnsType<InstallmentList> = [
    {
      width: 80,
      dataIndex: 'code',
      key: AccountPayableFieldOption.code,
      sorter: true,
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.code',
      }),
    },
    {
      width: 180,
      dataIndex: 'creditor',
      key: AccountPayableFieldOption.creditor,
      sorter: true,
      ellipsis: true,
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.creditor',
      }),
    },
    {
      width: 180,
      dataIndex: 'description',
      key: AccountPayableFieldOption.description,
      sorter: true,
      ellipsis: true,
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.description',
      }),
    },
    {
      width: 100,
      dataIndex: 'dueDate',
      key: AccountPayableFieldOption.dueDate,
      sorter: true,
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.dueDate',
      }),
      render: (date) => (date ? moment(date).format('DD/MM/YYYY') : '-'),
    },
    {
      width: 100,
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.situation',
      }),
      render: (situation: InstallmentSituation) => (
        <StatusTag
          color={AccountPayableSituationColor[situation]}
          label={InstallmentSituationType[situation]}
        />
      ),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'installmentValue',
      key: AccountPayableFieldOption.value,
      sorter: true,
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.value',
      }),
      render: (_, item) =>
        formatterPrice(
          item.installmentValue +
            (item.discountIncrease ?? 0) -
            (item.credit ?? 0),
        ),
    },
    {
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.actions',
      }),
      width: 50,
      align: 'right',
      fixed: 'right',
      key: 'actions',
      render: (row) => (
        <EditIcon
          disabled={
            !hasPermissions([Authorities.FINANCIAL_ACCOUNT_PAYABLE_EDIT]) ||
            installmentsIdsSelected?.includes(row.id)
          }
          iconProps={{
            onClick: () => setAccountPayableId(row.accountPayableId),
          }}
        />
      ),
    },
  ];

  function handleSorter(sorter: SorterResult<InstallmentList>) {
    if (!sorter.columnKey) return;

    const orderColumn =
      AccountPayableColumnConfig[
        sorter.columnKey as keyof typeof AccountPayableColumnConfig
      ];

    if (!orderColumn) return;

    setQueryParams((prev) => ({
      ...prev,
      orderColumns: orderColumn,
      order: sorter.order ? tableSorterOrder[sorter.order] : undefined,
    }));
  }

  return (
    <>
      <AppPageHeader
        isOpen
        searchValue={queryParams.search}
        onSearch={(search) => setQueryParams((prev) => ({ ...prev, search }))}
        headerActions={({ isShowingFilters }: { isShowingFilters: boolean }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() =>
                setQueryParams({
                  order: 'ASC',
                  bankAccountId,
                  orderColumns: AccountPayableOrderColumn.code,
                })
              }
            >
              Limpar Filtros
            </Button>
          )
        }
        extraFilters={
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={6} md={12}>
              <InputDate
                placeholder={intl.formatMessage({
                  id: 'releaseBillToPay.list.header.dueDate',
                })}
                value={
                  queryParams.dueDate ? moment(queryParams.dueDate) : undefined
                }
                onChange={(value) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    dueDate: value?.toISOString(),
                  }))
                }
              />
            </Col>

            <Col xs={24} lg={6} md={12}>
              <InputDecimal
                value={queryParams.installmentValue}
                onChange={(value) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    installmentValue: value,
                  }))
                }
                placeholder={intl.formatMessage({
                  id: 'releaseBillToPay.list.header.value',
                })}
              />
            </Col>

            <Select
              allowClear
              style={{ width: '100%' }}
              value={queryParams.situation}
              onChange={(value) =>
                setQueryParams((prev) => ({ ...prev, situation: value }))
              }
              placeholder={intl.formatMessage({
                id: 'releaseBillToPay.list.header.situation',
              })}
              options={[
                {
                  value: InstallmentSituation.open,
                  label: intl.formatMessage({
                    id: 'domain.enums.situation_installment_payable.open',
                  }),
                },
                {
                  value: InstallmentSituation.settled,
                  label: intl.formatMessage({
                    id: 'domain.enums.situation_installment_payable.settled',
                  }),
                },
              ]}
            />
          </Row>
        }
      />

      <InfiniteTable<InstallmentList>
        columns={columns}
        dataSource={items}
        loading={isFetching}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        rowKey={(row) => row.id}
        onChange={(_, __, sorter) =>
          !Array.isArray(sorter) && handleSorter(sorter)
        }
        onRow={(record) => {
          const linkedInfo = isLinkedToAnotherTransaction(record.id)
            ? getLinkedTransactionInfo(record.id)
            : null;

          return {
            title: linkedInfo ? `Vinculado à: ${linkedInfo}` : '',
          };
        }}
        rowSelection={{
          selectedRowKeys: getSelectedKeysVisibleInTable(),
          onChange: handleSelect,
          preserveSelectedRowKeys: true,
          getCheckboxProps: (item) => ({
            disabled: isLinkedToAnotherTransaction(item.id),
            className: isLinkedToAnotherTransaction(item.id)
              ? 'checkbox-disabled'
              : '',
          }),
        }}
      />

      <BillToPayEditModal
        open={!!accountPayableId}
        accountPayableId={accountPayableId}
        onCancel={() => setAccountPayableId(undefined)}
      />
    </>
  );
}
